
import { Component, Vue, Watch } from 'vue-property-decorator'
import { UserModule } from '@/store/modules/user'
import PlaformChart from '@/components/Charts/platform_chart.vue'
import CompanyChart from '@/components/Charts/company_chart.vue'
import StationChart from '@/components/Charts/station_chart.vue'
import { getDaysOfMonth, getTitleStringByType } from '@/utils/date'
import echart from 'echarts'
import mapJSON from '@/../public/map/xinjiang.json'
import { StationClient } from '@/api'

const QRCode = require('qrcode')

@Component({
  name: 'Dashboard',
  components: {
    PlaformChart,
    CompanyChart,
    StationChart
  }
})
export default class extends Vue {
  private stationClient = new StationClient()

  protected yearOptions: Array<{ value: number; label: number }> = []
  protected yearValue = new Date().getFullYear()

  protected quarterOptions: Array<{ value: number; label: string }> = []
  protected quarterValue = 1

  protected monthOptions: Array<{ value: number; label: string }> = []
  protected monthValue = new Date().getMonth() + 1

  protected dayOptions: Array<{ value: number; label: string }> = []
  protected dayValue = new Date().getDate()

  private stationData: any = null

  protected sectionTypeOptions = [
    {
      value: 'year',
      label: '按年统计'
    },
    {
      value: 'quarter',
      label: '按季度统计'
    },
    {
      value: 'month',
      label: '按月统计'
    },
    {
      value: 'day',
      label: '按天统计'
    }
  ]
  protected sectionTypeValue = 'year'

  get isShowQuarter() {
    return ['quarter'].includes(this.sectionTypeValue)
  }

  get isShowMonth() {
    return ['month', 'day'].includes(this.sectionTypeValue)
  }

  get isShowDay() {
    return ['day'].includes(this.sectionTypeValue)
  }

  get isPlatform() {
    return UserModule.companyId === null && UserModule.stationId === null
  }

  get isStation() {
    return UserModule.stationId != null
  }

  get isCompany() {
    return UserModule.companyId != null
  }

  get dashboardBadge() {
    if (this.isPlatform) {
      return 'Platform Overview'
    }
    if (this.isCompany) {
      return 'Fleet Dashboard'
    }
    return 'Station Dashboard'
  }

  get dashboardTitle() {
    if (this.isPlatform) {
      return '聚能迅达运营总览'
    }
    if (this.isCompany) {
      return '车队经营看板'
    }
    return '气站经营看板'
  }

  get dashboardDescription() {
    if (this.isPlatform) {
      return '把平台资金、消费趋势和重点分布收敛到同一页，减少反复切换菜单的成本。'
    }
    if (this.isCompany) {
      return '聚焦车队充值与消费走势，快速识别近期波动和异常变化。'
    }
    return '围绕当前气站的收款、消费结构和二维码收款入口做集中展示。'
  }

  get periodLabel() {
    return `当前统计范围：${getTitleStringByType(this.currentFilters)}`
  }

  get stationName() {
    return this.stationData && this.stationData.name
      ? this.stationData.name
      : '当前气站'
  }

  get currentFilters() {
    return {
      sectionType: this.sectionTypeValue,
      year: this.yearValue,
      quarter: this.quarterValue,
      month: this.monthValue,
      day: this.dayValue
    }
  }

  protected initData() {
    const today = new Date()
    this.yearOptions = []
    for (let i = 2019; i <= today.getFullYear(); i++) {
      this.yearOptions.push({
        value: i,
        label: i
      })
    }
    this.yearValue = today.getFullYear()
    this.quarterOptions = []
    for (let i = 1; i <= 4; i++) {
      this.quarterOptions.push({
        value: i,
        label: `第${i}季度`
      })
    }
    this.monthOptions = []
    for (let i = 1; i <= 12; i++) {
      this.monthOptions.push({
        value: i,
        label: `${i}月`
      })
    }
    this.monthValue = today.getMonth() + 1
    this.calculateDayFromYearAndMonth()
    this.dayValue = Math.min(today.getDate(), this.dayOptions.length)
  }

  @Watch('yearValue')
  private onYearValueChange() {
    this.calculateDayFromYearAndMonth()
  }

  @Watch('monthValue')
  private onMonthValueChange() {
    this.calculateDayFromYearAndMonth()
  }

  private calculateDayFromYearAndMonth() {
    const days = getDaysOfMonth(this.yearValue, this.monthValue)
    const dayOptions = []
    for (let i = 0; i < days; i++) {
      dayOptions.push({
        value: i + 1,
        label: `${i + 1}号`
      })
    }
    this.dayOptions = dayOptions
    if (this.dayValue > days) {
      this.dayValue = days
    }
  }

  created() {
    echart.registerMap('xinjiang', mapJSON)
    this.initData()
  }

  async mounted() {
    this.queryDataAndDraw()
    if (this.isStation) {
      this.stationData = await this.stationClient.get(UserModule.stationId)
      await this.$nextTick()
      this.renderStationQRCodes()
    }
  }

  private async renderStationQRCodes() {
    const lngCanvas = this.$refs.lngQrCanvas as HTMLCanvasElement
    const cngCanvas = this.$refs.cngQrCanvas as HTMLCanvasElement
    if (!lngCanvas || !cngCanvas) {
      return
    }
    const lngParam = encodeURIComponent(
      JSON.stringify({
        stationId: UserModule.stationId,
        gasType: 'LNG'
      })
    )
    const cngParam = encodeURIComponent(
      JSON.stringify({
        stationId: UserModule.stationId,
        gasType: 'CNG'
      })
    )
    const lngData = `https://www.xjjnxd.com/jnxunda/payment_static/${lngParam}`
    const cngData = `https://www.xjjnxd.com/jnxunda/payment_static/${cngParam}`
    QRCode.toCanvas(lngCanvas, lngData, (error: any) => {
      if (error) {
        console.error(error)
      }
    })
    QRCode.toCanvas(cngCanvas, cngData, (error: any) => {
      if (error) {
        console.error(error)
      }
    })
  }

  onQueryBtnTap() {
    this.queryDataAndDraw()
  }

  private queryDataAndDraw() {
    const filters = this.currentFilters
    this.$nextTick(() => {
      if (this.isPlatform) {
        (this.$refs.chartRef as PlaformChart).fetchChartsData(filters, 0)
      } else if (this.isCompany) {
        (this.$refs.chartRef as CompanyChart).fetchChartsData(
          filters,
          UserModule.companyId
        )
      } else if (this.isStation) {
        (this.$refs.chartRef as StationChart).fetchChartsData(
          filters,
          UserModule.stationId
        )
      }
    })
  }

  onLNGQrCodeBtnTap() {
    const canvas = this.$refs.lngQrCanvas as HTMLCanvasElement
    this.downloadCanvas(canvas, `${this.stationName}LNG收款二维码`)
  }

  onCNGQrCodeBtnTap() {
    const canvas = this.$refs.cngQrCanvas as HTMLCanvasElement
    this.downloadCanvas(canvas, `${this.stationName}CNG收款二维码`)
  }

  private downloadCanvas(canvas: HTMLCanvasElement, fileName: string) {
    if (!canvas) {
      return
    }
    const link = document.createElement('a')
    link.href = canvas.toDataURL('image/png')
    link.download = fileName
    link.click()
  }
}
